import { useSnackbar } from 'notistack';
import { useMemo, useState, useCallback } from 'react';

import { LoadingButton } from '@mui/lab';
import { DownloadOutlined } from '@mui/icons-material';
import {
  Alert,
  Paper,
  Stack,
  Table,
  Button,
  Dialog,
  Divider,
  TableRow,
  TableBody,
  TableCell,
  TableHead,
  Typography,
  DialogTitle,
  DialogActions,
  DialogContent,
  TableContainer,
  TablePagination,
} from '@mui/material';

import { useOrgTenant } from 'src/hooks/auth/use-org-tenant';
import { useAuthContext } from 'src/hooks/auth/use-auth-context';

import { useTranslate } from 'src/locales';
import { ListEntityType } from 'src/services/lists/lists.types';
import { CsvImportRow, CsvImportOptions } from 'src/services/common.types';
import { CustomFieldEntityType } from 'src/services/custom-fields/custom-field.types';
import { useGetConfigByEntityTypeQuery } from 'src/services/custom-fields/custom-field.service';

import CSVReader, { CSVReaderResult } from 'src/components/csv/csv-reader';

import { useFeatures } from 'src/sections/billing/usage/hooks/use-features';

import { TenantType } from 'src/types/enums';
import { FeatureType } from 'src/types/subscription.types';
import { BusinessErrorCodes } from 'src/types/business-errors';

import ImportResults from './import-results';
import { CsvImportResult } from './import-types';
import ImportColumnMapper from './import-column-mapper';
import { ImportEntity, FULL_NAME_KEY, ImportRequirement, IMPORT_ENTITY_CONFIGS } from './import-fields';
import ImportOptions, { importOptionsReady, ImportOptionsValue, DEFAULT_IMPORT_OPTIONS } from './import-options';
import {
  autoMatch,
  buildRows,
  sampleValues,
  displayHeader,
  ImportMapping,
  buildTemplateCsv,
  unmetRequirements,
  buildImportTargets,
  CUSTOM_FIELD_TARGET_PREFIX,
} from './import-mapping';

export type { CsvImportResult } from './import-types';

enum ImportStep {
  UPLOAD = 'upload',
  MAP = 'map',
  PREVIEW = 'preview',
  RESULTS = 'results',
}

/** How many skipped line numbers the preview lists before truncating. */
const SKIPPED_LINES_SHOWN = 10;

export type CsvImportDialogProps = {
  open: boolean;
  onClose: VoidFunction;
  /** Set when a super admin imports on an organisation's behalf. Its fields, custom fields and
   *  lists are the ones offered, and the server decides what its plan allows. */
  organizationId?: string;
  entity: ImportEntity;
  customFieldEntityType: CustomFieldEntityType;
  listEntityType: ListEntityType;
  /** Where this record type's existing strings live: title, preview_title, results_title,
   *  import_button and errors.import_failed. */
  i18nPrefix: string;
  templateFileName: string;
  isImporting: boolean;
  onImport: (rows: CsvImportRow[], options: CsvImportOptions) => Promise<CsvImportResult[]>;
  /** What identifies a result row to the user: its email or company name. */
  resultLabel: (result: CsvImportResult) => string;
  /** Show which company each contact was linked to. */
  showCompanyColumn?: boolean;
};

export default function CsvImportDialog({
  open,
  onClose,
  organizationId,
  entity,
  customFieldEntityType,
  listEntityType,
  i18nPrefix,
  templateFileName,
  isImporting,
  onImport,
  resultLabel,
  showCompanyColumn = false,
}: CsvImportDialogProps) {
  const { t } = useTranslate();
  const { enqueueSnackbar } = useSnackbar();
  const { canAccess } = useFeatures();
  const tenantType = useOrgTenant();
  const authContext = useAuthContext();

  const config = IMPORT_ENTITY_CONFIGS[entity];
  const onBehalfOfOrganization = Boolean(organizationId);
  const businessTenant = Boolean(authContext) && tenantType !== TenantType.Candidate;

  // A tenant's own plan gates these here, as the record editor does. On an organisation's behalf
  // the signed-in plan says nothing about theirs, so the server decides: it returns no configs
  // when custom fields are off and refuses a list when Lists are.
  const customFieldsEnabled = onBehalfOfOrganization || (businessTenant && canAccess(FeatureType.CUSTOM_FIELDS_ENABLED));
  const listsEnabled = onBehalfOfOrganization || (businessTenant && canAccess(FeatureType.LISTS_ENABLED));

  const { data: customFields } = useGetConfigByEntityTypeQuery(
    { entityType: customFieldEntityType, organizationId },
    { skip: !open || !customFieldsEnabled }
  );

  const [step, setStep] = useState<ImportStep>(ImportStep.UPLOAD);
  const [headers, setHeaders] = useState<string[]>([]);
  const [dataRows, setDataRows] = useState<unknown[][]>([]);
  const [mapping, setMapping] = useState<ImportMapping>({});
  const [options, setOptions] = useState<ImportOptionsValue>(DEFAULT_IMPORT_OPTIONS);
  const [results, setResults] = useState<CsvImportResult[]>([]);
  const [importedListName, setImportedListName] = useState<string | undefined>();
  const [error, setError] = useState<string | null>(null);

  const [previewPage, setPreviewPage] = useState(0);
  const [previewRowsPerPage, setPreviewRowsPerPage] = useState(10);

  const targets = useMemo(
    () => buildImportTargets(config, customFieldsEnabled ? customFields : undefined, t),
    [config, customFields, customFieldsEnabled, t]
  );

  const labelFor = useCallback(
    (id: string) => targets.find((target) => target.id === id)?.label ?? id,
    [targets]
  );

  const samples = useMemo(() => headers.map((_, column) => sampleValues(dataRows, column)), [headers, dataRows]);

  const missingRequirements = useMemo(() => unmetRequirements(mapping, config), [mapping, config]);

  const built = useMemo(() => buildRows(dataRows, mapping, config), [dataRows, mapping, config]);

  // The preview shows what will be sent, so a full name column appears as the two names it becomes.
  const previewColumns = useMemo(() => {
    const ids = Object.keys(mapping)
      .map(Number)
      .sort((a, b) => a - b)
      .map((column) => mapping[column])
      .filter(Boolean)
      .reduce<string[]>((all, id) => all.concat(id === FULL_NAME_KEY ? ['first_name', 'last_name'] : [id]), []);

    return ids.filter((id, index) => ids.indexOf(id) === index);
  }, [mapping]);

  const describeRequirement = useCallback(
    (requirement: ImportRequirement) =>
      requirement.alternatives
        .map((alternative) =>
          alternative
            .map(labelFor)
            .reduce((first, second) => t('csv_import.both', { first, second }))
        )
        .reduce((first, second) => t('csv_import.either', { first, second })),
    [labelFor, t]
  );

  const reset = useCallback(() => {
    setStep(ImportStep.UPLOAD);
    setHeaders([]);
    setDataRows([]);
    setMapping({});
    setOptions(DEFAULT_IMPORT_OPTIONS);
    setResults([]);
    setImportedListName(undefined);
    setError(null);
    setPreviewPage(0);
  }, []);

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [onClose, reset]);

  const handleDownloadTemplate = useCallback(() => {
    const blob = new Blob([buildTemplateCsv(targets)], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = templateFileName;
    link.click();
    URL.revokeObjectURL(url);
  }, [targets, templateFileName]);

  const handleCSVRead = useCallback(
    (parsed: CSVReaderResult) => {
      setError(null);

      const [headerRow, ...rows] = (parsed.data ?? []) as unknown[][];

      if (!headerRow || headerRow.every((cell) => !String(cell ?? '').trim())) {
        setError(t('csv_import.errors.empty_file'));
        return;
      }

      if (!rows.some((cells) => (cells ?? []).some((cell) => String(cell ?? '').trim()))) {
        setError(t('csv_import.errors.no_data_rows'));
        return;
      }

      const fileHeaders = headerRow.map((cell) => displayHeader(String(cell ?? '')));

      setHeaders(fileHeaders);
      setDataRows(rows);
      setMapping(autoMatch(fileHeaders, targets));
      setStep(ImportStep.MAP);
    },
    [t, targets]
  );

  const handleMappingChange = useCallback((column: number, targetId: string) => {
    setMapping((current) => ({ ...current, [column]: targetId }));
  }, []);

  const handleImport = useCallback(async () => {
    const newListName = options.newListName.trim();

    let list: CsvImportOptions['list'];
    let listName: string | undefined;
    if (options.listMode === 'existing' && options.existingList) {
      list = { id: options.existingList.id };
      listName = options.existingList.name;
    }
    if (options.listMode === 'new' && newListName) {
      list = { name: newListName };
      listName = newListName;
    }

    try {
      setResults(await onImport(built.rows, { on_duplicate: options.onDuplicate, list }));
      setImportedListName(listName);
      setStep(ImportStep.RESULTS);
    } catch (err: any) {
      let message = t(`${i18nPrefix}.errors.import_failed`);
      if (err?.status === 403) message = t('exceptions.unauthorized');
      if (err?.data?.error_code === BusinessErrorCodes.ListNameInUse) message = t('csv_import.errors.list_name_in_use');

      enqueueSnackbar(message, { variant: 'error' });
    }
  }, [built.rows, enqueueSnackbar, i18nPrefix, onImport, options, t]);

  const renderUploadStep = () => (
    <Stack spacing={3}>
      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Alert severity="info">
        {t('csv_import.upload_instructions')}
        {customFields && customFields.length > 0 && ` ${t('csv_import.custom_fields_in_template')}`}
      </Alert>

      <CSVReader onRead={handleCSVRead} />
    </Stack>
  );

  const renderMapStep = () => (
    <Stack spacing={2}>
      <Alert severity="info">{t('csv_import.map_instructions')}</Alert>

      {missingRequirements.length > 0 ? (
        <Alert severity="warning">
          {t('csv_import.missing_required', { fields: missingRequirements.map(describeRequirement).join(', ') })}
        </Alert>
      ) : (
        <Alert severity="success">{t('csv_import.all_required_matched')}</Alert>
      )}

      <ImportColumnMapper
        headers={headers}
        samples={samples}
        targets={targets}
        mapping={mapping}
        onChange={handleMappingChange}
      />
    </Stack>
  );

  const renderPreviewStep = () => {
    const { rows, skippedLines } = built;
    const pageRows = rows.slice(previewPage * previewRowsPerPage, (previewPage + 1) * previewRowsPerPage);
    const lines =
      skippedLines.slice(0, SKIPPED_LINES_SHOWN).join(', ') + (skippedLines.length > SKIPPED_LINES_SHOWN ? ', ...' : '');

    const cellValue = (row: CsvImportRow, id: string): string => {
      if (id.startsWith(CUSTOM_FIELD_TARGET_PREFIX)) {
        const customValues = row.custom_fields as Record<string, string> | undefined;
        return customValues?.[id.slice(CUSTOM_FIELD_TARGET_PREFIX.length)] ?? '';
      }
      return (row[id] as string | undefined) ?? '';
    };

    return (
      <Stack spacing={2}>
        {rows.length > 0 ? (
          <Alert severity="success">{t('csv_import.preview_ready', { count: rows.length })}</Alert>
        ) : (
          <Alert severity="error">{t('csv_import.errors.no_valid_rows')}</Alert>
        )}

        {skippedLines.length > 0 && (
          <Alert severity="warning">{t('csv_import.preview_skipped', { count: skippedLines.length, lines })}</Alert>
        )}

        <ImportOptions
          value={options}
          onChange={setOptions}
          listEntityType={listEntityType}
          listsEnabled={listsEnabled}
          organizationId={organizationId}
        />

        <Divider />

        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 420 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                {previewColumns.map((id) => (
                  <TableCell key={id} sx={{ whiteSpace: 'nowrap' }}>
                    {labelFor(id)}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {pageRows.map((row, index) => (
                <TableRow key={index} hover>
                  {previewColumns.map((id) => (
                    <TableCell key={id}>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 220 }}>
                        {cellValue(row, id) || '-'}
                      </Typography>
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={rows.length}
          page={previewPage}
          onPageChange={(_, page) => setPreviewPage(page)}
          rowsPerPage={previewRowsPerPage}
          onRowsPerPageChange={(event) => {
            setPreviewRowsPerPage(parseInt(event.target.value, 10));
            setPreviewPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Stack>
    );
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        {step === ImportStep.UPLOAD && (
          <Stack direction="row" alignItems="center" spacing={1} justifyContent="space-between">
            <Typography variant="inherit">{t(`${i18nPrefix}.title`)}</Typography>

            <Button startIcon={<DownloadOutlined />} onClick={handleDownloadTemplate}>
              {t('candidates.invites.candidate_invite_modal.download_template')}
            </Button>
          </Stack>
        )}
        {step === ImportStep.MAP && t('csv_import.map_title')}
        {step === ImportStep.PREVIEW && t(`${i18nPrefix}.preview_title`)}
        {step === ImportStep.RESULTS && t(`${i18nPrefix}.results_title`)}
      </DialogTitle>

      <DialogContent>
        {step === ImportStep.UPLOAD && renderUploadStep()}
        {step === ImportStep.MAP && renderMapStep()}
        {step === ImportStep.PREVIEW && renderPreviewStep()}
        {step === ImportStep.RESULTS && (
          <ImportResults
            results={results}
            resultLabel={resultLabel}
            labelFor={labelFor}
            showCompanyColumn={showCompanyColumn}
            listName={importedListName}
          />
        )}
      </DialogContent>

      <DialogActions>
        {step === ImportStep.UPLOAD && <Button onClick={handleClose}>{t('common.cancel')}</Button>}

        {step === ImportStep.MAP && (
          <>
            <Button onClick={reset}>{t('common.back')}</Button>
            <Button
              variant="contained"
              disabled={missingRequirements.length > 0}
              onClick={() => {
                setPreviewPage(0);
                setStep(ImportStep.PREVIEW);
              }}
            >
              {t('common.continue')}
            </Button>
          </>
        )}

        {step === ImportStep.PREVIEW && (
          <>
            <Button onClick={() => setStep(ImportStep.MAP)}>{t('common.back')}</Button>
            <LoadingButton
              variant="contained"
              onClick={handleImport}
              loading={isImporting}
              disabled={built.rows.length === 0 || !importOptionsReady(options)}
            >
              {t(`${i18nPrefix}.import_button`)}
            </LoadingButton>
          </>
        )}

        {step === ImportStep.RESULTS && (
          <Button variant="contained" onClick={handleClose}>
            {t('common.close')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
